'use client'

import { useEffect, useState } from 'react'
import { t } from '@lingui/core/macro'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme, alpha } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import BiotechIcon from '@mui/icons-material/Biotech'
import BarChartIcon from '@mui/icons-material/BarChart'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LockIcon from '@mui/icons-material/Lock'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import SecurityIcon from '@mui/icons-material/Security'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'

const CONSENT_STORAGE_PREFIX = 'patient_data_reuse_consent_'

type PatientDataReuseConsentDialogProps = {
  consultationId: string
  onComplete: (accepted: boolean) => void
  existingConsent?: boolean | null
}

export function PatientDataReuseConsentDialog({ consultationId, onComplete, existingConsent }: PatientDataReuseConsentDialogProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [open, setOpen] = useState(true)
  const [currentStep, setCurrentStep] = useState<'info' | 'consent'>('info')
  const [consentChecked, setConsentChecked] = useState(false)

  // Auto-close if consent already given
  useEffect(() => {
    const hasStored = localStorage.getItem(`${CONSENT_STORAGE_PREFIX}${consultationId}`)
    if (hasStored !== null || (existingConsent !== undefined && existingConsent !== null)) {
      setOpen(false)
      onComplete(hasStored === 'true' || existingConsent === true)
    }
  }, [consultationId, existingConsent, onComplete])

  const handleContinue = () => {
    if (currentStep === 'info') {
      setCurrentStep('consent')
      return
    }
    localStorage.setItem(`${CONSENT_STORAGE_PREFIX}${consultationId}`, 'true')
    setOpen(false)
    onComplete(true)
  }

  const handleRefuse = () => {
    localStorage.setItem(`${CONSENT_STORAGE_PREFIX}${consultationId}`, 'false')
    setOpen(false)
    onComplete(false)
  }

  const handleBack = () => {
    setCurrentStep('info')
  }

  const purposes = [
    {
      icon: <BiotechIcon sx={{ color: theme.palette.primary.main }} />,
      title: t`Recherche Médicale`,
      desc: t`Identifier de nouveaux traitements via l'analyse de données anonymisées.`,
      bgColor: alpha(theme.palette.primary.main, 0.08),
    },
    {
      icon: <MonitorHeartIcon sx={{ color: theme.palette.info.main }} />,
      title: t`Amélioration des Soins`,
      desc: t`Personnaliser vos parcours de soins pour une meilleure réactivité médicale.`,
      bgColor: alpha(theme.palette.info.main, 0.08),
    },
    {
      icon: <BarChartIcon sx={{ color: theme.palette.secondary.dark }} />,
      title: t`Analyses Statistiques`,
      desc: t`Optimiser les ressources hospitalières et améliorer la qualité du service.`,
      bgColor: alpha(theme.palette.secondary.main, 0.08),
    },
  ]

  const rights = [
    t`Conformité RGPD & anonymisation stricte.`,
    t`Hébergement sécurisé (Données de Santé).`,
    t`Contrôle total : droit d'accès, de rectification et d'opposition.`,
  ]

  if (!open) return null

  return (
    <Dialog
      disableEscapeKeyDown
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '16px',
          overflow: 'hidden',
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 4, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {currentStep === 'consent' && (
              <IconButton onClick={handleBack} size="small" sx={{ color: theme.palette.primary.main }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: '12px',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontSize: '0.65rem',
              }}
            >
              {currentStep === 'info' ? t`Étape 1 sur 2` : t`Étape 2 sur 2`}
            </Typography>
          </Stack>
        </Box>

        {currentStep === 'info' ? (
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            {t`Pourquoi vos données`}{' '}
            <Box component="span" sx={{ color: theme.palette.primary.main }}>
              {t`comptent ?`}
            </Box>
          </Typography>
        ) : (
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em', lineHeight: 1.2, textAlign: 'center' }}>
            {t`Votre accord`}
          </Typography>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ px: 3, pb: 3, overflowY: 'auto', flex: 1 }}>
        {currentStep === 'info' ? (
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {t`En partageant vos données de manière sécurisée, vous permettez des avancées majeures pour la santé de tous.`}
            </Typography>

            {/* Purpose cards */}
            <Stack spacing={1.5}>
              {purposes.map((purpose, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.08),
                  }}
                >
                  <Box
                    sx={{
                      mt: 0.25,
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '10px',
                      bgcolor: purpose.bgColor,
                      flexShrink: 0,
                    }}
                  >
                    {purpose.icon}
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>
                      {purpose.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.8rem' }}>
                      {purpose.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>

            {/* Rights & Security */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: '12px',
                bgcolor: alpha(theme.palette.grey[200], 0.4),
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <SecurityIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {t`Vos droits & sécurité`}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {rights.map((right, i) => (
                  <Stack key={i} direction="row" alignItems="flex-start" spacing={1}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.primary.main, mt: 0.1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {right}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.7 }}>
              {t`Nous avons besoin de votre consentement explicite pour intégrer vos données dans nos protocoles d'amélioration et de recherche.`}
            </Typography>

            {/* Guarantee cards */}
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: '10px' }}>
                <VerifiedUserIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                    {t`Protection Totale`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {t`Anonymisation stricte selon les normes RGPD.`}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: '10px' }}>
                <BiotechIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                    {t`Usage Encadré`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {t`Recherche médicale et amélioration du service uniquement.`}
                  </Typography>
                </Box>
              </Box>
            </Stack>

            {/* Consent checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  sx={{
                    color: theme.palette.grey[400],
                    '&.Mui-checked': { color: theme.palette.primary.main },
                  }}
                />
              }
              label={
                <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.6, fontSize: '0.85rem' }}>
                  {t`J'ai bien compris ces informations et j'accepte que mes données soient réutilisées pour ces finalités`}
                </Typography>
              }
              sx={{ alignItems: 'flex-start', mx: 0 }}
            />
          </Stack>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 3,
          bgcolor: alpha(theme.palette.grey[100], 0.5),
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {currentStep === 'info' ? (
          <Button
            onClick={handleContinue}
            variant="contained"
            fullWidth
            endIcon={<ArrowForwardIcon />}
            sx={{
              height: isMobile ? 52 : 48,
              borderRadius: '12px',
              fontWeight: 700,
              fontSize: '0.95rem',
              textTransform: 'none',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              },
            }}
          >
            {t`Continuer`}
          </Button>
        ) : (
          <>
            <Button
              onClick={handleContinue}
              variant="contained"
              fullWidth
              disabled={!consentChecked}
              sx={{
                height: isMobile ? 52 : 48,
                borderRadius: '12px',
                fontWeight: 700,
                fontSize: '0.95rem',
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                },
                '&.Mui-disabled': {
                  background: theme.palette.grey[300],
                  color: theme.palette.grey[500],
                },
              }}
            >
              {t`J'accepte`}
            </Button>
            <Button
              onClick={handleRefuse}
              fullWidth
              sx={{
                height: isMobile ? 48 : 44,
                borderRadius: '12px',
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {t`Refuser`}
            </Button>
          </>
        )}

        {/* Protocol footer */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 0.5 }}>
          <LockIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}
          >
            {t`MedConnect - Protection des données`}
          </Typography>
        </Stack>
      </Box>
    </Dialog>
  )
}
